import type { StoreApi } from "zustand"
import { createStore } from "zustand"

import type { PurchaseOrderCalculatedTotals, PurchaseOrderPricingLineResult } from "@/graphql/codegen/graphql"

export enum DeliveryTermsFieldsEnum {
  DELIVERY_TERMS = "deliveryTerms",
  DELIVERY_LOCATION_ID = "deliveryLocationId",
  FREIGHT_TERMS = "freightTerms",
  SHIP_VIA_COMPANY_ID = "shipViaCompanyId",
  SHIP_STATUS = "shipStatus",
}

/**
 * PurchaseOrderState interface defines the state and actions for the purchase order store.
 * It can be used to store additional data needed for PO management.
 */
export interface PurchaseOrderPricing {
  calculatedPricing: {
    orderLines: PurchaseOrderPricingLineResult[]
    totals?: PurchaseOrderCalculatedTotals | null
  } | null
  fetching: boolean
}

export interface PurchaseOrderState {
  orderLineRoundedValueMap: Map<number, { roundingEnabled: boolean; isBagsWholeNumber?: boolean }>
  pricing: PurchaseOrderPricing
  // Actions
  setPricing: (pricing: PurchaseOrderPricing) => void
  setPricingFetching: (fetching: boolean) => void
  setOrderLineRounded: (id: number, isBagsWholeNumber: boolean) => void
  clearOrderLineRounded: (id: number) => void
  resetStore: () => void
}
/**
 * usePurchaseOrderStore is a Zustand store that manages the state of the purchase order.
 * It provides actions to update the state and is wrapped with the devtools middleware for debugging.
 *
 * @returns A hook that can be used to access and modify the purchase order state.
 */
export const createPurchaseOrderStore = (): StoreApi<PurchaseOrderState> =>
  createStore<PurchaseOrderState>((set) => ({
    orderLineRoundedValueMap: new Map(),

    setOrderLineRounded: (id, isBagsWholeNumber) => {
      return set((state) => {
        const updatedMap = new Map(state.orderLineRoundedValueMap)
        updatedMap.set(id, { roundingEnabled: true, isBagsWholeNumber })
        return { orderLineRoundedValueMap: updatedMap }
      })
    },

    clearOrderLineRounded: (id) =>
      set((state) => {
        const updatedMap = new Map(state.orderLineRoundedValueMap)
        updatedMap.set(id, { roundingEnabled: false })
        return { orderLineRoundedValueMap: updatedMap }
      }),
    pricing: { calculatedPricing: null, fetching: false },
    setPricing: (pricing: PurchaseOrderPricing) => set({ pricing }),
    setPricingFetching: (fetching: boolean) => set((state) => ({ pricing: { ...state.pricing, fetching } })),
    resetStore: () =>
      set({
        orderLineRoundedValueMap: new Map(),
        pricing: { calculatedPricing: null, fetching: false },
      }),
  }))
